import {
  mergeAndZipVideoChunks,
  upload,
  uploadVideo,
  uploadVideoChunk,
} from "../api/UploadAPI";
import { BASE_URL } from "../constants/ApiEndpointConstants";

// 参考分片页面中的实际数值：10 * 1024 * 1024，即每片 10 MiB。
export const FACE_VIDEO_CHUNK_SIZE = 10 * 1024 * 1024;
export const FACE_VIDEO_CHUNK_MAX_ATTEMPTS = 3;
const FACE_VIDEO_CHUNK_RETRY_DELAY_MS = 400;

const createUploadId = () =>
  window.crypto?.randomUUID?.() ||
  `face-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const createAbortError = () => {
  const error = new Error("视频分片上传已取消。");
  error.name = "AbortError";
  return error;
};

const waitForRetry = (delayMs, signal) =>
  new Promise((resolve, reject) => {
    if (!delayMs) {
      resolve();
      return;
    }
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const timer = window.setTimeout(resolve, delayMs);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(createAbortError());
      },
      { once: true },
    );
  });

const uploadChunkWithRetry = async (request, options) => {
  const {
    maxAttempts = FACE_VIDEO_CHUNK_MAX_ATTEMPTS,
    retryDelayMs = FACE_VIDEO_CHUNK_RETRY_DELAY_MS,
    signal,
  } = options;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (signal?.aborted) throw createAbortError();

    try {
      return await uploadVideoChunk({ ...request, signal });
    } catch (error) {
      if (signal?.aborted || attempt === maxAttempts) throw error;
      options.onRetry?.({
        chunkIndex: request.chunkIndex,
        attempt,
        maxAttempts,
        error,
      });
      await waitForRetry(retryDelayMs * 2 ** (attempt - 1), signal);
    }
  }

  throw new Error("视频分片上传失败。");
};

const getVideoFileName = (blob, filePrefix, uploadId) => {
  const mimeType = blob?.type || "video/webm";
  const extension = mimeType.toLowerCase().includes("mp4") ? "mp4" : "webm";
  return `${filePrefix}-${uploadId}.${extension}`;
};

const getMergedFileUrl = (response) => {
  const result = response?.data?.data || response?.data;
  const path = result?.paths?.[0] || result?.zip_path || result?.fullurl;
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;

  return `${BASE_URL.replace(/\/$/, "")}/${String(path).replace(/^\/+/, "")}`;
};

export const uploadFile = async (file, snCode) => {
  if (!file) {
    return null;
  }

  try {
    const imgUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const res = await upload(imgUrl, snCode);
    const url = res?.data?.data?.fullurl || null;

    return url;
  } catch (error) {
    console.error("文件上传失败：", error);
    return null;
  }
};

export const uploadVideoFile = async (
  blob,
  snCode,
  filePrefix = "face-video",
) => {
  if (!blob) return;
  try {
    const res = await uploadVideo(blob, snCode, filePrefix);
    const url = res?.data?.data?.fullurl || null;
    return url;
  } catch (error) {
    console.error("视频上传失败：", error);
    return null;
  }
};

export const uploadChunkedVideoFile = async (
  blob,
  snCode,
  filePrefix = "face-video",
  options = {},
) => {
  if (!blob?.size) return null;

  const {
    chunkSize = FACE_VIDEO_CHUNK_SIZE,
    uploadId = createUploadId(),
    // folderName 是服务端识别同一批分片的依据，默认每次上传都生成唯一目录。
    folderName = uploadId,
    onProgress,
    onStart,
    onMergeStart,
    onMergeComplete,
    signal,
  } = options;
  const mimeType = blob.type || "video/webm";
  const fileName = getVideoFileName(blob, filePrefix, uploadId);
  const totalChunks = Math.max(1, Math.ceil(blob.size / chunkSize));

  try {
    onStart?.({ totalChunks, chunkSize, fileSize: blob.size });

    // 按序上传，保证服务端可以通过 index 从 0 开始还原原始文件。
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, blob.size);

      await uploadChunkWithRetry(
        {
          chunk: blob.slice(start, end, mimeType),
          snCode,
          folderName,
          chunkIndex,
          totalChunks,
          fileName,
          mimeType,
        },
        options,
      );

      onProgress?.({
        uploadedChunks: chunkIndex + 1,
        totalChunks,
        progress: (chunkIndex + 1) / totalChunks,
      });
    }

    if (signal?.aborted) throw createAbortError();

    // 只有全部分片成功后才通知服务端合并，不能使用某个分片的返回地址。
    onMergeStart?.({ folderName, totalChunks, fileName });
    const mergeResponse = await mergeAndZipVideoChunks({
      folderName,
      snCode,
      signal,
    });
    const mergedFileUrl = getMergedFileUrl(mergeResponse);

    if (!mergedFileUrl) {
      throw new Error("服务端未返回合并后的 zip_path。");
    }

    onMergeComplete?.({ folderName, fileName });
    return mergedFileUrl;
  } catch (error) {
    if (!signal?.aborted) {
      console.error("[活体检测] 视频分片上传结果：失败", error);
    }
    return null;
  }
};

export const uploadFaceVideo = (blob, ssn, options) =>
  uploadChunkedVideoFile(blob, ssn, "face-video", options);
